import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Memoir, Todo, CreateMemoirRequest, UpdateMemoirRequest, CreateTodoRequest, PaginatedResponse, APIError } from '../../types/api';
import { apiClient } from '../../services/api';
import type { RootState } from '../index';

interface MemoirState {
  memoirs: Memoir[];
  currentMemoir: Memoir | null;
  todos: Todo[];
  isLoading: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  error: string | null;
  pagination: {
    total: number;
    page: number;
    pages: number;
    size: number;
  };
}

const initialState: MemoirState = {
  memoirs: [],
  currentMemoir: null,
  todos: [],
  isLoading: false,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  error: null,
  pagination: {
    total: 0,
    page: 1,
    pages: 0,
    size: 20,
  },
};

// Async thunks
export const fetchMemoirs = createAsyncThunk<
  PaginatedResponse<Memoir>,
  { page?: number; size?: number },
  { rejectValue: APIError }
>(
  'memoir/fetchMemoirs',
  async ({ page = 1, size = 20 }, { rejectWithValue }) => {
    try {
      const response = await apiClient.getMemoirs(page, size);
      return response;
    } catch (error) {
      return rejectWithValue(error as APIError);
    }
  }
);

export const fetchMemoir = createAsyncThunk<
  Memoir,
  string,
  { rejectValue: APIError }
>(
  'memoir/fetchMemoir',
  async (memoirId, { rejectWithValue }) => {
    try {
      const memoir = await apiClient.getMemoir(memoirId);
      return memoir;
    } catch (error) {
      return rejectWithValue(error as APIError);
    }
  }
);

export const createMemoir = createAsyncThunk<
  Memoir,
  CreateMemoirRequest,
  { rejectValue: APIError }
>(
  'memoir/createMemoir',
  async (memoirData, { rejectWithValue }) => {
    try {
      const memoir = await apiClient.createMemoir(memoirData);
      return memoir;
    } catch (error) {
      return rejectWithValue(error as APIError);
    }
  }
);

export const updateMemoir = createAsyncThunk<
  Memoir,
  { memoirId: string; data: UpdateMemoirRequest },
  { rejectValue: APIError }
>(
  'memoir/updateMemoir',
  async ({ memoirId, data }, { rejectWithValue }) => {
    try {
      const memoir = await apiClient.updateMemoir(memoirId, data);
      return memoir;
    } catch (error) {
      return rejectWithValue(error as APIError);
    }
  }
);

export const deleteMemoir = createAsyncThunk<
  string,
  string,
  { rejectValue: APIError }
>(
  'memoir/deleteMemoir',
  async (memoirId, { rejectWithValue }) => {
    try {
      await apiClient.deleteMemoir(memoirId);
      return memoirId;
    } catch (error) {
      return rejectWithValue(error as APIError);
    }
  }
);

// Todo async thunks
export const fetchTodos = createAsyncThunk<
  Todo[],
  string,
  { rejectValue: APIError }
>(
  'memoir/fetchTodos',
  async (memoirId, { rejectWithValue }) => {
    try {
      return await apiClient.getTodos(memoirId);
    } catch (error) {
      return rejectWithValue(error as APIError);
    }
  }
);

export const createTodo = createAsyncThunk<
  Todo,
  { memoirId: string; data: CreateTodoRequest },
  { rejectValue: APIError }
>(
  'memoir/createTodo',
  async ({ memoirId, data }, { rejectWithValue }) => {
    try {
      return await apiClient.createTodo(memoirId, data);
    } catch (error) {
      return rejectWithValue(error as APIError);
    }
  }
);

export const updateTodo = createAsyncThunk<
  Todo,
  { todoId: string; data: Partial<CreateTodoRequest & { is_completed: boolean }> },
  { rejectValue: APIError }
>(
  'memoir/updateTodo',
  async ({ todoId, data }, { rejectWithValue }) => {
    try {
      return await apiClient.updateTodo(todoId, data);
    } catch (error) {
      return rejectWithValue(error as APIError);
    }
  }
);

export const deleteTodo = createAsyncThunk<
  string,
  string,
  { rejectValue: APIError }
>(
  'memoir/deleteTodo',
  async (todoId, { rejectWithValue }) => {
    try {
      await apiClient.deleteTodo(todoId);
      return todoId;
    } catch (error) {
      return rejectWithValue(error as APIError);
    }
  }
);

const memoirSlice = createSlice({
  name: 'memoir',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentMemoir: (state, action: PayloadAction<Memoir | null>) => {
      state.currentMemoir = action.payload;
    },
    updateCurrentMemoir: (state, action: PayloadAction<Partial<Memoir>>) => {
      if (state.currentMemoir) {
        state.currentMemoir = { ...state.currentMemoir, ...action.payload };
      }
    },
    clearMemoirs: (state) => {
      state.memoirs = [];
      state.currentMemoir = null;
      state.pagination = initialState.pagination;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Memoirs
      .addCase(fetchMemoirs.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchMemoirs.fulfilled, (state, action) => {
        state.isLoading = false;
        state.memoirs = action.payload.items || [];
        state.pagination = {
          total: action.payload.total,
          page: action.payload.page,
          pages: action.payload.pages,
          size: action.payload.size,
        };
        state.error = null;
      })
      .addCase(fetchMemoirs.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.detail || 'Failed to fetch memoirs';
      })
      // Fetch Memoir
      .addCase(fetchMemoir.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchMemoir.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentMemoir = action.payload;
        state.error = null;

        // Update memoir in list if it exists
        const index = state.memoirs.findIndex(m => m.memoir_id === action.payload.memoir_id);
        if (index !== -1) {
          state.memoirs[index] = action.payload;
        }
      })
      .addCase(fetchMemoir.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.detail || 'Failed to fetch memoir';
      })
      // Create Memoir
      .addCase(createMemoir.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createMemoir.fulfilled, (state, action) => {
        state.isCreating = false;
        // Ensure memoirs array exists before unshifting
        if (!state.memoirs) {
          state.memoirs = [];
        }
        state.memoirs.unshift(action.payload);
        state.currentMemoir = action.payload;
        state.error = null;
      })
      .addCase(createMemoir.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload?.detail || 'Failed to create memoir';
      })
      // Update Memoir
      .addCase(updateMemoir.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateMemoir.fulfilled, (state, action) => {
        state.isUpdating = false;
        const index = state.memoirs.findIndex(m => m.memoir_id === action.payload.memoir_id);
        if (index !== -1) {
          state.memoirs[index] = action.payload;
        }
        if (state.currentMemoir?.memoir_id === action.payload.memoir_id) {
          state.currentMemoir = action.payload;
        }
        state.error = null;
      })
      .addCase(updateMemoir.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload?.detail || 'Failed to update memoir';
      })
      // Delete Memoir
      .addCase(deleteMemoir.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
      })
      .addCase(deleteMemoir.fulfilled, (state, action) => {
        state.isDeleting = false;
        state.memoirs = state.memoirs.filter(m => m.memoir_id !== action.payload);
        if (state.currentMemoir?.memoir_id === action.payload) {
          state.currentMemoir = null;
        }
        state.error = null;
      })
      .addCase(deleteMemoir.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload?.detail || 'Failed to delete memoir';
      })
      // Fetch Todos
      .addCase(fetchTodos.fulfilled, (state, action) => {
        state.todos = action.payload;
      })
      // Create Todo
      .addCase(createTodo.fulfilled, (state, action) => {
        state.todos.push(action.payload);
      })
      // Update Todo
      .addCase(updateTodo.fulfilled, (state, action) => {
        const index = state.todos.findIndex(t => t.todo_id === action.payload.todo_id);
        if (index !== -1) {
          state.todos[index] = action.payload;
        }
      })
      // Delete Todo
      .addCase(deleteTodo.fulfilled, (state, action) => {
        state.todos = state.todos.filter(t => t.todo_id !== action.payload);
      });
  },
});

export const { clearError, setCurrentMemoir, updateCurrentMemoir, clearMemoirs } = memoirSlice.actions;
export default memoirSlice.reducer;

// Selectors
export const selectMemoirs = (state: RootState) => state.memoir?.memoirs || [];
export const selectCurrentMemoir = (state: RootState) => state.memoir?.currentMemoir || null;
export const selectMemoirLoading = (state: RootState) => state.memoir?.isLoading || false;
export const selectMemoirCreating = (state: RootState) => state.memoir?.isCreating || false;
export const selectMemoirUpdating = (state: RootState) => state.memoir?.isUpdating || false;
export const selectMemoirDeleting = (state: RootState) => state.memoir?.isDeleting || false;
export const selectMemoirError = (state: RootState) => state.memoir?.error || null;
export const selectMemoirPagination = (state: RootState) => state.memoir?.pagination || { total: 0, page: 1, pages: 0, size: 20 };
export const selectMemoirTodos = (state: RootState) => state.memoir?.todos || [];